import { useCallback, useMemo } from 'react'
import { FlagxsColumnDef } from '../../../containers/BulkSheetView/gridOptions/extension'
import {
  useAccountable,
  useActualEndDate,
  useActualStartDate,
  useAssignee,
  useAttachments,
  useCode,
  useCommentSummaryAction,
  useDifficulty,
  useEstimatedProgressRate,
  useEstimatedStoryPoint,
  useEstimatedWorkLoadDeliverable,
  useParentWbsItem,
  usePath,
  usePriority,
  useResponsible,
  useScheduledEndDate,
  useScheduledStartDate,
  useStatus,
  useTags,
  useWatchers,
  useProgressRate,
  usePlannedValue,
  useEarnedValue,
  usePreceding,
  useDelayed,
  useRemaining,
  useActualHour,
  useCostVariance,
  useScheduleVariance,
  useSchedulePerformanceIndex,
  useCostPerformanceIndex,
  useEstimateToComplete,
  useEstimateAtCompletion,
  useVarianceAtCompletion,
  useSprints,
  useDescription,
  useLatestComment,
} from '../../../page-properties/bulksheet-properties/wbsItem'
import { intl } from '../../../../i18n'
import { ColumnType } from '../../../containers/commons/AgGrid'
import {
  getDeliverableTotalVarianceCellStyle,
  getDeliverableSprintVarianceCellStyle,
  getTaskVarianceCellStyle,
  getTotalTaskVarianceCellStyle,
  findSprintProductItem,
} from './varianceUtils'
import { SprintDetail, SprintStatus } from '../../../../lib/functions/sprint'
import {
  getWbsItemStatusColorCode,
  getWbsItemStatusDeepColorCode,
} from '../../../../lib/functions/wbsItem'
import { WbsItemStatus } from '../../../../domain/entity/WbsItemEntity'
import {
  ProductBacklogItemRow,
  ProductBacklogRow,
  getWorkload,
  isProductBacklogItemRow,
  isSprintRow,
  createSprintProductItemKey,
  createSprintProductItemAssigneeKey,
  createSprintProductItemSprintGoalKey,
  SprintRow,
  SprintProductItemDetail,
} from '../productBacklog'
import {
  CellClassParams,
  CellClickedEvent,
  EditableCallbackParams,
  ICellRendererParams,
  IRowNode,
  RowDragCallback,
  RowDragCallbackParams,
  RowNode,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community'
import { allDescendants } from '../../../containers/BulkSheetView/lib/gridApi'
import { toNumber } from '../../../../utils/number'
import Workload, {
  getWorkloadUnitValue,
  WorkloadUnit,
} from '../../../../lib/functions/workload'
import { WbsItemSelectColumnOptionType } from '../../../hooks/useWbsItemSelectColumnOptions'
import { CustomEnumValue } from '../../../../lib/commons/appFunction'
import {
  useCreatedAt,
  useCreatedBy,
  useRevision,
  useUpdatedAt,
  useUpdatedBy,
} from '../../../page-properties/bulksheet-properties/changeLog'
import { ProductBacklogRowNumberCellRenderer } from '../components/ProductBacklogRowNumberCellRenderer'
import { useTreeDragAndDrop } from '../../../page-properties/bulksheet-properties/dragAndDrop'
import store from '../../../../store'
import { requireSave } from '../../../../store/requiredSaveData'
import { ProductBacklogStatusFilter } from '../components/ProductBacklogStatusFilter'
import EstimatedWorkloadCellRenderer, {
  DeliverableTotalCellRenderer,
  TotalTaskEstimatedWorkloadCellRenderer,
  TaskEstimatedWorkloadCellRenderer,
} from '../components/EstimatedWorkloadCellRenderer'
import { EntitySearchCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import { ProjectMemberCellEditor } from '../../../containers/BulkSheetView/components/cellEditor/ProjectMemberCellEditor'
import { ClientSideSelectFilter } from '../../../containers/BulkSheetView/components/filter'
import { UserBasic } from '../../../../lib/functions/user'
import { SprintAdjustmentActionCellRenderer } from '../components/SprintAdjustmentActionCellRenderer'

export const sprintBasedComparator = (
  _valueA: any,
  _valueB: any,
  nodeA: any,
  nodeB: any
) => {
  const rowA = nodeA.data as ProductBacklogRow
  const rowB = nodeB.data as ProductBacklogRow

  // スプリント行同士の比較
  if (isSprintRow(rowA) && isSprintRow(rowB)) {
    // スプリント未設定は常に最後
    if (rowA.isUnset && !rowB.isUnset) return 1
    if (!rowA.isUnset && rowB.isUnset) return -1
    if (rowA.isUnset && rowB.isUnset) return 0

    // スプリント番号で比較
    const numA = rowA.sprintDetail?.num ?? Number.MAX_SAFE_INTEGER
    const numB = rowB.sprintDetail?.num ?? Number.MAX_SAFE_INTEGER
    return numA - numB
  }

  // 成果物行同士の比較（親のスプリントで比較）
  if (isProductBacklogItemRow(rowA) && isProductBacklogItemRow(rowB)) {
    // 同じスプリント内なら元の順序を維持
    if (rowA.treeValue[0] === rowB.treeValue[0]) return 0

    // 親スプリント行を直接取得して比較
    const sprintA = nodeA.parent?.data as SprintRow | undefined
    const sprintB = nodeB.parent?.data as SprintRow | undefined

    if (sprintA && sprintB) {
      // スプリント未設定は常に最後
      if (sprintA.isUnset && !sprintB.isUnset) return 1
      if (!sprintA.isUnset && sprintB.isUnset) return -1
      if (sprintA.isUnset && sprintB.isUnset) return 0

      // スプリント番号で比較
      const numA = sprintA.sprintDetail?.num ?? Number.MAX_SAFE_INTEGER
      const numB = sprintB.sprintDetail?.num ?? Number.MAX_SAFE_INTEGER
      return numA - numB
    }

    return 0
  }

  // スプリント行と成果物行の比較
  if (isSprintRow(rowA)) return -1
  if (isSprintRow(rowB)) return 1

  return 0
}

// Default workload standard when workloadUnitState is not available
// Using 1 as divisor prevents division errors and returns raw hour values
const DEFAULT_WORKLOAD_STANDARD = {
  dailyWorkHours: 1,
  monthlyWorkDays: 1,
} as const

// Convert hours to workload unit value using context's workloadUnitState
const toWorkloadUnitValue = (
  hours: number,
  context: {
    workloadUnitState?: {
      dailyWorkHours: number
      monthlyWorkDays: number
      selectedWorkloadUnit?: WorkloadUnit
    }
  }
): number => {
  const workloadStandard =
    context.workloadUnitState || DEFAULT_WORKLOAD_STANDARD
  const workload = Workload.from({ hour: hours, standard: workloadStandard })
  return getWorkloadUnitValue(
    workload,
    context.workloadUnitState?.selectedWorkloadUnit
  )
}

/**
 * フィルター適用後の子行のみを取得
 * node.childrenAfterFilterを使用してフィルター適用後の直接の子を取得
 */
const getFilteredLeafChildren = (
  node: IRowNode<ProductBacklogRow> | null | undefined
): IRowNode<ProductBacklogRow>[] => {
  if (!node?.data || !isSprintRow(node.data)) return []

  return (
    node.childrenAfterFilter?.filter(
      child => child.data && isProductBacklogItemRow(child.data)
    ) || []
  )
}

// Get sprint product item value from edited data or sprintProductItems
const getSprintEstimatedHour = (
  pbiRow: ProductBacklogItemRow,
  sprintUuid: string,
  rowUuid?: string
): number => {
  // 行UUIDを含めた一意のキーを生成
  const sprintProductItemKey = createSprintProductItemKey(sprintUuid, rowUuid)

  const editedValue = pbiRow.editedData?.[sprintProductItemKey]
  if (editedValue !== undefined) {
    return editedValue || 0
  }

  // 行UUID（SprintProductItemのUUID）で検索、なければsprintUuidで検索
  const sprintProductItem = rowUuid
    ? (pbiRow.sprintProductItems?.find(spi => spi.uuid === rowUuid) ??
      pbiRow.sprintProductItems?.find(spi => spi.sprintUuid === sprintUuid))
    : pbiRow.sprintProductItems?.find(spi => spi.sprintUuid === sprintUuid)
  return sprintProductItem?.estimatedHour || 0
}

// Common handler for sprint product item cell value changes
const handleSprintProductItemChange = (
  params: ValueSetterParams<ProductBacklogRow>,
  shouldConvertFromWorkloadUnit: boolean = false
): boolean => {
  if (!params.data || !isProductBacklogItemRow(params.data)) return false

  const pbiRow = params.data
  const parentSprintUuid = pbiRow.treeValue[0]
  if (!parentSprintUuid) return false

  const parsedValue = toNumber(params.newValue)
  const inputValue = parsedValue !== undefined ? parsedValue : 0

  let hourValue = inputValue

  // Convert from workload unit to hours if needed
  if (shouldConvertFromWorkloadUnit) {
    const workloadStandard =
      params.context.workloadUnitState || DEFAULT_WORKLOAD_STANDARD
    const workloadConfig = {
      [WorkloadUnit.HOUR]: { hour: inputValue },
      [WorkloadUnit.DAY]: { day: inputValue },
      [WorkloadUnit.MONTH]: { month: inputValue },
    }
    const selectedUnit =
      params.context.workloadUnitState?.selectedWorkloadUnit ||
      WorkloadUnit.HOUR
    const config = workloadConfig[selectedUnit] || { hour: inputValue }
    const newWorkload = Workload.from({
      ...config,
      standard: workloadStandard,
    })
    hourValue = getWorkloadUnitValue(newWorkload, WorkloadUnit.HOUR)
  }

  updateSprintProductItemHour(
    pbiRow,
    parentSprintUuid,
    hourValue,
    params.context.incrementDataVersion
  )

  return true
}

// implicit未設定行のセル編集時にSPIを自動作成するためのデフォルトSPI
const createDefaultSprintProductItem = (
  pbiRow: ProductBacklogItemRow,
  sprintUuid: string
): SprintProductItemDetail => ({
  uuid: pbiRow.uuid,
  sprintUuid,
  estimatedHour: 0,
  taskEstimatedHour: 0,
  displayOrder: 0,
  lockVersion: 0,
  assignee: pbiRow.assignee,
  sprintGoal: pbiRow.sprintGoal ?? null,
})

// Update sprint product item hour value
const updateSprintProductItemHour = (
  pbiRow: ProductBacklogItemRow,
  parentSprintUuid: string,
  hourValue: number,
  incrementDataVersion?: () => void
): void => {
  // 行UUIDを含めた一意のキーを生成
  const sprintProductItemKey = createSprintProductItemKey(
    parentSprintUuid,
    pbiRow.uuid
  )

  if (!pbiRow.editedData) {
    pbiRow.editedData = {}
  }

  // 行UUID（SprintProductItemのUUID）で検索
  // NOTE: sprintUuidでのフォールバック検索は行わない
  // 同一スプリントに複数のSprintProductItemが存在しうるため、
  // sprintUuidでの検索は誤った項目を見つける可能性がある
  const existingIndex =
    pbiRow.sprintProductItems?.findIndex(spi => spi.uuid === pbiRow.uuid) ?? -1
  const currentSprintProductItem =
    existingIndex >= 0 ? pbiRow.sprintProductItems[existingIndex] : undefined

  if (pbiRow.editedData[sprintProductItemKey] === undefined) {
    pbiRow.editedData[sprintProductItemKey] =
      currentSprintProductItem?.estimatedHour || 0
  }

  pbiRow.editedData[sprintProductItemKey] = hourValue

  if (existingIndex >= 0) {
    pbiRow.sprintProductItems[existingIndex] = {
      ...pbiRow.sprintProductItems[existingIndex],
      estimatedHour: hourValue,
    }
  } else {
    // SPIが存在しない場合は新規作成（implicit未設定行の編集時）
    if (!pbiRow.sprintProductItems) {
      pbiRow.sprintProductItems = []
    }
    const newSpi = createDefaultSprintProductItem(pbiRow, parentSprintUuid)
    newSpi.estimatedHour = hourValue
    pbiRow.sprintProductItems.push(newSpi)
  }

  pbiRow.edited = true
  store.dispatch(requireSave())

  // データバージョンを更新してポップアップの再計算をトリガー
  if (incrementDataVersion) {
    incrementDataVersion()
  }
}

// Get sprint row estimated workload in selected workload unit
const getSprintRowEstimatedWorkload = (
  params: ValueGetterParams<ProductBacklogRow>
): number => {
  if (!params.data || !isSprintRow(params.data)) return 0

  // フィルター適用後の子行のみを集計
  const childPbiRows = getFilteredLeafChildren(params.node)
  const totalHours = childPbiRows.reduce((total, row) => {
    if (!row.data || !isProductBacklogItemRow(row.data)) return total
    const pbiRow = row.data
    // DISCARD状態の成果物は集計から除外
    if (pbiRow.wbsItem.status === WbsItemStatus.DISCARD) return total
    const parentSprintUuid = pbiRow.treeValue?.[0]
    if (!parentSprintUuid) return total
    return total + getSprintEstimatedHour(pbiRow, parentSprintUuid, pbiRow.uuid)
  }, 0)

  return toWorkloadUnitValue(totalHours, params.context)
}

// Get PBI row estimated workload in selected workload unit
const getPBIRowEstimatedWorkload = (
  params: ValueGetterParams<ProductBacklogRow>
): number => {
  if (!params.data || !isProductBacklogItemRow(params.data)) return 0

  const pbiRow = params.data
  const parentSprintUuid = pbiRow.treeValue[0]
  if (!parentSprintUuid) return 0

  const hourValue = getSprintEstimatedHour(
    pbiRow,
    parentSprintUuid,
    pbiRow.uuid
  )
  return toWorkloadUnitValue(hourValue, params.context)
}

// PBIからタスク見積もり時間を抽出する関数の型
type TaskEstimateExtractor = (
  pbiRow: ProductBacklogItemRow,
  sprintUuid: string
) => number

// スプリント単位のタスク見積もりを抽出
const extractSprintTaskEstimate: TaskEstimateExtractor = (
  pbiRow,
  sprintUuid
) => {
  return findSprintProductItem(pbiRow, sprintUuid)?.taskEstimatedHour || 0
}

// 全体のタスク見積もりを抽出（スプリント関係なく）
const extractTotalTaskEstimate: TaskEstimateExtractor = pbiRow => {
  if (!pbiRow.cumulation) return 0
  return (
    pbiRow.cumulation.sumTaskEstimatedHour -
    pbiRow.cumulation.sumTaskEstimatedHourDiscard
  )
}

// Sprint行のタスク見積もり合計を取得する共通関数
const getSprintRowTaskEstimate = (
  params: ValueGetterParams<ProductBacklogRow>,
  extractEstimate: TaskEstimateExtractor
): number => {
  if (!params.data || !isSprintRow(params.data)) return 0

  const sprintUuid = params.data.uuid
  const childPbiRows = getFilteredLeafChildren(params.node)

  // 同じ成果物（wbsItem.uuid）が重複しないようにユニーク化
  const seenWbsItemUuids = new Set<string>()
  const totalHours = childPbiRows.reduce((total, row) => {
    if (!row.data || !isProductBacklogItemRow(row.data)) return total
    const pbiRow = row.data
    // DISCARD状態の成果物は集計から除外
    if (pbiRow.wbsItem.status === WbsItemStatus.DISCARD) return total
    // 同じ成果物は1回だけカウント
    const wbsItemUuid = pbiRow.wbsItem.uuid
    if (!wbsItemUuid || seenWbsItemUuids.has(wbsItemUuid)) return total
    seenWbsItemUuids.add(wbsItemUuid)

    return total + extractEstimate(pbiRow, sprintUuid)
  }, 0)

  return toWorkloadUnitValue(totalHours, params.context)
}

// PBI行のタスク見積もりを取得する共通関数
const getPBIRowTaskEstimate = (
  params: ValueGetterParams<ProductBacklogRow>,
  extractEstimate: TaskEstimateExtractor
): number => {
  if (!params.data || !isProductBacklogItemRow(params.data)) return 0

  const pbiRow = params.data
  const sprintUuid = pbiRow.treeValue[0]
  return toWorkloadUnitValue(
    extractEstimate(pbiRow, sprintUuid),
    params.context
  )
}

// タスク見積もり列のvalueGetterを生成
const createTaskEstimateValueGetter =
  (extractor: TaskEstimateExtractor) =>
  (params: ValueGetterParams<ProductBacklogRow>): number | undefined => {
    const data = params.data
    if (!data) return undefined

    if (isSprintRow(data)) {
      return getSprintRowTaskEstimate(params, extractor)
    }

    if (isProductBacklogItemRow(data)) {
      return getPBIRowTaskEstimate(params, extractor)
    }

    return undefined
  }

/*
 * ProductBacklog Column Def
 */
export interface PropsForGetColumnDefs {
  projectUuid: string
  projectCode: string
  selectColumnOptions: WbsItemSelectColumnOptionType
  onClickedStatusCell?: (
    target: EventTarget | undefined,
    row: ProductBacklogItemRow | undefined
  ) => void
  reloadSingleRow?: ((uuid: string) => void) | undefined
  submitSingleRow?: (uuid: string) => Promise<void>
  incrementDataVersion?: () => void
  openSprintAdjustmentDialog?: (row: ProductBacklogItemRow) => void
}

export const useColumnDefs = ({
  projectCode,
  selectColumnOptions,
  onClickedStatusCell,
  reloadSingleRow,
  submitSingleRow,
  incrementDataVersion,
  openSprintAdjustmentDialog,
}: PropsForGetColumnDefs): FlagxsColumnDef[] => {
  const getWbsItemUuidFromNode = useCallback(
    (node: RowNode<ProductBacklogRow>) => {
      if (!node || !node.data || !isProductBacklogItemRow(node.data)) {
        return undefined
      }
      return node.data.wbsItem.uuid
    },
    []
  )
  const getWbsItem = useCallback(
    (row: ProductBacklogRow | undefined) =>
      row && isProductBacklogItemRow(row) ? row.wbsItem : undefined,
    []
  )
  const getWbsItemType = useCallback(
    (row: ProductBacklogRow | undefined) =>
      row && isProductBacklogItemRow(row) ? row.wbsItem.wbsItemType : undefined,
    []
  )
  const getBaseWbsItemType = useCallback(
    (row: ProductBacklogRow | undefined) =>
      row && isProductBacklogItemRow(row)
        ? row.wbsItem.baseWbsItemType
        : undefined,
    []
  )
  const getTeamUuid = useCallback(
    (row: ProductBacklogRow | undefined) =>
      row && isProductBacklogItemRow(row) ? row.wbsItem.team?.uuid : undefined,
    []
  )
  const getScheduledDate = useCallback((row: ProductBacklogRow | undefined) => {
    return row && isProductBacklogItemRow(row)
      ? row.wbsItem.scheduledDate
      : undefined
  }, [])
  const getActualDate = useCallback(
    (row: ProductBacklogRow | undefined) =>
      row && isProductBacklogItemRow(row) ? row.wbsItem.actualDate : undefined,
    []
  )
  const getWbsItemStatus = useCallback(
    (row: ProductBacklogRow | undefined) =>
      row && isProductBacklogItemRow(row) ? row.wbsItem.status : undefined,
    []
  )
  const getWbsItemProjectUuid = useCallback(
    (row: ProductBacklogRow | undefined) =>
      row && isProductBacklogItemRow(row) ? row.wbsItem.projectUuid : undefined,
    []
  )

  const getCumulation = useCallback(
    (row: ProductBacklogRow | undefined) =>
      row && isProductBacklogItemRow(row) ? row.cumulation : undefined,
    []
  )
  const getCommentSummary = useCallback(
    (row: ProductBacklogRow | undefined) =>
      row && isProductBacklogItemRow(row) ? row.commentSummary : undefined,
    []
  )
  const getAttachmentSummary = useCallback(
    (row: ProductBacklogRow | undefined) =>
      row && isProductBacklogItemRow(row)
        ? row.deliverableAttachmentSummary
        : undefined,
    []
  )
  const getParentWbsItem = useCallback(
    (row: ProductBacklogRow | undefined) =>
      row && isProductBacklogItemRow(row) ? row.parentWbsItem : undefined,
    []
  )

  //   const clearSprint = useCallback(
  //     (params: ValueSetterParams<ProductBacklogRow>) => {
  //       if (!params.data?.wbsItem?.sprint) return
  //       if (!params.data.editedData) {
  //         params.data.editedData = {
  //           [FIELD_SPRINT]: params.data.wbsItem.sprint,
  //         }
  //       } else if (!params.data.editedData.hasOwnProperty(FIELD_SPRINT)) {
  //         params.data.editedData[FIELD_SPRINT] = params.data.wbsItem.sprint
  //       }
  //       params.data.wbsItem.sprint = undefined
  //     },
  //     []
  //   )

  const rowDrag = useCallback<RowDragCallback<ProductBacklogRow>>(
    (params: RowDragCallbackParams<ProductBacklogRow>) => {
      // ProductBacklogItemのみドラッグ可能
      return Boolean(params.data && isProductBacklogItemRow(params.data))
    },
    []
  )
  const dragAndDropCol = useTreeDragAndDrop<ProductBacklogRow>({
    rowDrag,
  })

  // Basic information group column common def
  const codeCol = useCode('wbsItem.code')

  const statusCombinedValuePath = (combinedEnumCode: string) => {
    switch (combinedEnumCode) {
      case 'WBS_TYPE':
        return 'wbsItem.type'
      case 'TICKET_TYPE':
        return 'wbsItem.ticketType'
      case 'WBS_STATUS':
        return 'wbsItem.status'
      case 'WBS_SUBSTATUS':
        return 'wbsItem.substatus'
    }
  }

  const statusCol = useStatus({
    field: 'wbsItem.status',
    subStatusField: 'wbsItem.substatus',
    statusCombinedValuePath,
  })
  const actionCol = useCommentSummaryAction({
    field: 'action',
    getCommentSummary,
    getWbsItem,
  })
  const attachmentsCol = useAttachments({
    field: 'deliverableAttachmentSummary',
    getWbsItemUuid: getWbsItemUuidFromNode,
    getAttachmentSummary,
  })
  const tagsCol = useTags({
    field: 'wbsItem.tags',
    getWbsItemProjectUuid,
  })

  // Path group column common def
  const pathCol = usePath('projectPlanPath')
  const parentWbsItemCol = useParentWbsItem({
    field: 'parentWbsItem',
    getParentWbsItem,
  })

  // Name group column common def
  const priorityCol = usePriority({
    field: 'wbsItem.priority',
    customEnumOptions: selectColumnOptions
      ? (selectColumnOptions['priority'] as CustomEnumValue[])
      : [],
  })
  const difficultyCol = useDifficulty({
    field: 'wbsItem.difficulty',
    customEnumOptions: selectColumnOptions
      ? (selectColumnOptions['difficulty'] as CustomEnumValue[])
      : [],
  })

  // Explanation group column common def
  const descriptionCol = useDescription('wbsItem.description')
  const latestCommentCol = useLatestComment('commentSummary.latestComment')

  //   // Assignee group column common def
  //   const teamCol = useTeam({
  //     field: 'wbsItem.team',
  //     gridOptionContext: selectColumnOptionMap,
  //     clearSprint,
  //   })
  const accountableCol = useAccountable({
    field: 'wbsItem.accountable',
  })
  const responsibleCol = useResponsible({
    field: 'wbsItem.responsible',
  })
  const assigneeCol = useAssignee({
    field: 'wbsItem.assignee',
  })
  const watchersCol = useWatchers({
    field: 'wbsItem.watchers',
  })

  // Estimated group column common def
  const estimatedWorkLoadDeliverableCol = useEstimatedWorkLoadDeliverable({
    field: FIELD_WBSITEM_ESTIMATED_WORKLOAD_DELIVERABLE,
    getWbsItemType,
  })
  const estimatedStoryPoint = useEstimatedStoryPoint(
    'wbsItem.estimatedStoryPoint'
  )

  // Progress group column common def
  const estimatedProgressRateCol = useEstimatedProgressRate(
    'estimatedProgressRate',
    getWbsItem,
    getCumulation
  )
  const progressRateCol = useProgressRate(
    'progressRate',
    getWbsItem,
    getCumulation
  )
  const plannedValueCol = usePlannedValue(
    'plannedValue',
    getWbsItem,
    getCumulation
  )
  const earnedValueCol = useEarnedValue(
    'earnedValue',
    getWbsItem,
    getCumulation
  )
  const precedingCol = usePreceding('preceding', getWbsItem, getCumulation)
  const delayedCol = useDelayed('delayedWorkload', getWbsItem, getCumulation)
  const remainingCol = useRemaining(
    'remainingWorkload',
    getWbsItem,
    getCumulation
  )

  // Productivity group column common def
  const actualHourCol = useActualHour({
    field: 'wbsItem.actualHour',
    getWbsItemType: getBaseWbsItemType,
    getCumulation,
    getWbsItem,
  })
  const costVarianceCol = useCostVariance(
    'costVariance',
    getWbsItem,
    getCumulation
  )
  const costPerformanceIndexCol = useCostPerformanceIndex(
    'costPerformanceIndex',
    getWbsItem,
    getCumulation
  )
  const scheduleVarianceCol = useScheduleVariance(
    'scheduleVariance',
    getWbsItem,
    getCumulation
  )
  const schedulePerformanceIndexCol = useSchedulePerformanceIndex(
    'schedulePerformanceIndex',
    getWbsItem,
    getCumulation
  )
  const estimateToCompleteCol = useEstimateToComplete(
    'estimateToComplete',
    getWbsItem,
    getCumulation
  )
  const estimateAtCompletionCol = useEstimateAtCompletion(
    'estimateAtCompletion',
    getWbsItem,
    getCumulation
  )
  const varianceAtCompletionCol = useVarianceAtCompletion(
    'varianceAtCompletion',
    getWbsItem,
    getCumulation
  )

  // Term group column common def
  const sprintsCol = useSprints({
    field: FIELD_WBSITEM_SPRINTS,
    hide: false,
  })

  const setScheduledEndDate = useCallback(
    (
      params: ValueSetterParams<ProductBacklogItemRow>,
      newValue: string,
      oldValue: string
    ) => {
      if (!params.data?.wbsItem?.scheduledDate) {
        params.data.wbsItem.scheduledDate = {}
      }
      params.data.wbsItem.scheduledDate.endDate = newValue
      if (!params.data.editedData) {
        params.data.editedData = {}
      }
      params.data.editedData['wbsItem.scheduledDate.endDate'] = oldValue
    },
    []
  )
  const scheduledStartDateCol = useScheduledStartDate({
    field: 'wbsItem.scheduledDate.startDate',
    getScheduledDate,
    setScheduledEndDate,
  })

  const setScheduledStartDate = useCallback(
    (
      params: ValueSetterParams<ProductBacklogItemRow>,
      newValue: string,
      oldValue: string
    ) => {
      if (!params.data?.wbsItem?.scheduledDate) {
        params.data.wbsItem.scheduledDate = {}
      }
      params.data.wbsItem.scheduledDate.startDate = newValue
      if (!params.data.editedData) {
        params.data.editedData = {}
      }
      params.data.editedData['wbsItem.scheduledDate.startDate'] = oldValue
    },
    []
  )
  const scheduledEndDateCol = useScheduledEndDate({
    field: 'wbsItem.scheduledDate.endDate',
    getScheduledDate,
    setScheduledStartDate,
  })

  const setActualEndDate = useCallback(
    (
      params: ValueSetterParams<ProductBacklogRow>,
      newValue: string,
      oldValue: string
    ) => {
      if (!params.data || !isProductBacklogItemRow(params.data)) return
      const pbiRow = params.data
      if (!pbiRow.wbsItem.actualDate) {
        pbiRow.wbsItem.actualDate = {}
      }
      pbiRow.wbsItem.actualDate.endDate = newValue
      if (!pbiRow.editedData) {
        pbiRow.editedData = {}
      }
      pbiRow.editedData['wbsItem.actualDate.endDate'] = oldValue
    },
    []
  )
  const actualStartDateCol = useActualStartDate({
    field: 'wbsItem.actualDate.startDate',
    getWbsItemStatus,
    getScheduledDate,
    getActualDate,
    setActualEndDate,
  })

  const setActualStartDate = useCallback(
    (
      params: ValueSetterParams<ProductBacklogRow>,
      newValue: string,
      oldValue: string
    ) => {
      if (!params.data || !isProductBacklogItemRow(params.data)) return
      const pbiRow = params.data
      if (!pbiRow.wbsItem.actualDate) {
        pbiRow.wbsItem.actualDate = {}
      }
      pbiRow.wbsItem.actualDate.startDate = newValue
      if (!pbiRow.editedData) {
        pbiRow.editedData = {}
      }
      pbiRow.editedData['wbsItem.actualDate.startDate'] = oldValue
    },
    []
  )
  const actualEndDateCol = useActualEndDate({
    field: 'wbsItem.actualDate.endDate',
    getWbsItemStatus,
    getScheduledDate,
    getActualDate,
    setActualStartDate,
  })

  // Update info group column common def
  const revisionCol = useRevision('wbsItem.revision')
  const createdByCol = useCreatedBy('wbsItem.createdBy')
  const createdAtCol = useCreatedAt('wbsItem.createdAt')
  const updatedByCol = useUpdatedBy('wbsItem.updatedBy')
  const updatedAtCol = useUpdatedAt('wbsItem.updatedAt')

  const columnDefs = useMemo(
    () => [
      {
        externalId: 'drag',
        ...dragAndDropCol,
        sortable: false, // TODO Remove and implement after considering sorting specification.
      },
      {
        externalId: 'rowNumber',
        field: 'rowNumber',
        type: [ColumnType.sequenceNo],
        sortable: false, // TODO Remove and implement after considering sorting specification.
        valueGetter: (params: ValueGetterParams<ProductBacklogItemRow>) => {
          return (params?.node?.rowIndex ?? 0) + 1
        },
        cellRenderer: ProductBacklogRowNumberCellRenderer,
        cellRendererParams: {
          projectCode,
        },
      },
      // Basic information
      {
        externalId: 'productBacklog.basic',
        headerName: intl.formatMessage({ id: 'projectPlan.information' }),
        children: [
          {
            externalId: 'productBacklog.wbsItem.code',
            ...codeCol,
            editable: false,
            valueGetter: (params: ValueGetterParams<ProductBacklogRow>) => {
              if (!params.data) return undefined
              // スプリント行はコードを表示しない
              if (isSprintRow(params.data)) {
                return undefined
              }
              if (isProductBacklogItemRow(params.data)) {
                return params.data.wbsItem.code
              }
              return undefined
            },
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
            sortable: false, // TODO Implement after considering sorting specification.
          },
          {
            externalId: 'productBacklog.wbsItem.status',
            ...statusCol,
            lockPosition: 'left',
            sortable: false, // TODO Remove and implement after considering sorting specification.
            editable: params => {
              if (!params.data) return false
              if (isSprintRow(params.data)) return false
              return EDITABLE(params)
            },
            valueGetter: (params: ValueGetterParams<ProductBacklogRow>) => {
              if (!params.data) return undefined
              if (isSprintRow(params.data)) {
                return params.data.sprintDetail?.status
              }
              if (isProductBacklogItemRow(params.data)) {
                return params.data.wbsItem.status
              }
              return undefined
            },
            valueFormatter: params => {
              if (!params.data) return ''
              if (
                isSprintRow(params.data) &&
                params.data.sprintDetail?.status
              ) {
                // ステータス値をそのまま返す（INPROGRESS, STANDBY等）
                return params.data.sprintDetail.status
              }
              if (isProductBacklogItemRow(params.data)) {
                // WbsItemのステータス表示は既存のstatusColに任せる
                return typeof statusCol.valueFormatter === 'function'
                  ? statusCol.valueFormatter(params)
                  : params.value
              }
              return params.value || ''
            },
            cellRenderer: (params: ICellRendererParams<ProductBacklogRow>) => {
              if (!params.data) return null
              if (isSprintRow(params.data)) {
                if (params.data.sprintDetail?.status) {
                  // ステータス値をそのまま表示（INPROGRESS, STANDBY等）
                  const sprintStatusText = params.data.sprintDetail.status

                  // 進捗情報を追加
                  const { completed, total } = getSprintProgress(params)
                  return `${sprintStatusText} (${completed} / ${total})`
                }
                return ''
              }
              // ProductBacklogItemRowの場合はデフォルトのcellRendererを使用
              if (
                statusCol.cellRenderer &&
                isProductBacklogItemRow(params.data)
              ) {
                return statusCol.cellRenderer(params)
              }
              return params.value
            },
            onCellClicked: (e: CellClickedEvent<ProductBacklogRow>) => {
              if (
                !onClickedStatusCell ||
                !e.data ||
                !isProductBacklogItemRow(e.data)
              ) {
                return
              }
              onClickedStatusCell(e.event?.target ?? undefined, e.data)
            },
            cellStyle: (params: CellClassParams<ProductBacklogRow>) => {
              if (
                params.data &&
                isSprintRow(params.data) &&
                params.data.sprintDetail?.status
              ) {
                return {
                  backgroundColor: getSprintStatusColor(
                    params.data.sprintDetail.status
                  ),
                  color: '#333',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }
              }
              if (typeof statusCol.cellStyle === 'function') {
                return statusCol.cellStyle(params)
              }
              return statusCol.cellStyle
            },
            // WbsItemのステータスとスプリントのステータス両方でフィルター可能にする
            // スプリント行: スプリントステータスでフィルター
            // PBI行: 親スプリントステータス + 自身のWbsItemステータスの両方でフィルター
            //   → 親スプリントがフィルターで除外されると、そのPBIも除外される
            filter: ProductBacklogStatusFilter,
            floatingFilter: true,
            filterParams: {
              getLabel: (v: { status: string; type: string }) => {
                return v.status
              },
              sortValues: (
                options: Array<
                  | { status: string; type: string }
                  | Array<{ status: string; type: string }>
                >
              ) => {
                // スプリントステータスを先に、その後WbsItemステータスをソート
                const sprintOrder = [
                  SprintStatus.STANDBY,
                  SprintStatus.INPROGRESS,
                  SprintStatus.FINISHED,
                  SprintStatus.CANCELED,
                ]
                return options.sort((rawA, rawB) => {
                  const a = Array.isArray(rawA) ? rawA[0] : rawA
                  const b = Array.isArray(rawB) ? rawB[0] : rawB
                  if (!a || !b) return 0
                  // 同じタイプ同士の比較
                  if (a.type === b.type) {
                    if (a.type === 'sprint') {
                      return (
                        sprintOrder.indexOf(a.status as SprintStatus) -
                        sprintOrder.indexOf(b.status as SprintStatus)
                      )
                    }
                    return wbsItemStatusComparator(
                      a.status as WbsItemStatus,
                      b.status as WbsItemStatus
                    )
                  }
                  // スプリントを先に表示
                  return a.type === 'sprint' ? -1 : 1
                })
              },
            },
          },
          {
            externalId: 'productBacklog.action',
            ...actionCol,
            lockPosition: false,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.sprintAdjustmentAction',
            field: 'sprintAdjustmentAction',
            headerName: intl.formatMessage({
              id: 'productBacklog.sprintAdjustmentAction',
            }),
            hide: false,
            pinned: false,
            lockPosition: false,
            sortable: false,
            width: 60,
            cellRenderer: SprintAdjustmentActionCellRenderer,
            cellRendererParams: {
              openSprintAdjustmentDialog,
            },
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.attachment',
            ...attachmentsCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellRenderer: params =>
              isProductBacklogItemRow(params.data)
                ? attachmentsCol.cellRenderer(params)
                : undefined,
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.wbsItem.tags',
            ...tagsCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            editable: EDITABLE,
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
        ] as FlagxsColumnDef[],
      },
      // Estimated
      {
        externalId: 'productBacklog.estimatedWorkload',
        headerName: intl.formatMessage({ id: 'projectPlan.estimated' }),
        children: [
          {
            externalId: 'productBacklog.wbsItem.estimatedWorkload',
            ...estimatedWorkLoadDeliverableCol,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            editable: EDITABLE,
            cellStyle: getDeliverableTotalVarianceCellStyle,
            cellRenderer: DeliverableTotalCellRenderer,
            valueSetter: (params: ValueSetterParams<ProductBacklogRow>) => {
              if (!isProductBacklogItemRow(params.data)) {
                return false
              }
              const value = toNumber(params.newValue)
              const workloadStandard =
                params.context.workloadUnitState || DEFAULT_WORKLOAD_STANDARD
              let newWorkload: Workload
              switch (params.context.workloadUnitState?.selectedWorkloadUnit) {
                case WorkloadUnit.HOUR:
                  newWorkload = Workload.from({
                    hour: value,
                    standard: workloadStandard,
                  })
                  break
                case WorkloadUnit.DAY:
                  newWorkload = Workload.from({
                    day: value,
                    standard: workloadStandard,
                  })
                  break
                case WorkloadUnit.MONTH:
                  newWorkload = Workload.from({
                    month: value,
                    standard: workloadStandard,
                  })
                  break
                default:
                  newWorkload = Workload.from({
                    hour: value,
                    standard: workloadStandard,
                  })
                  break
              }
              params.data.edited = true
              if (!params.data.editedData) {
                params.data.editedData = {}
              }
              params.data.editedData['wbsItem.estimatedWorkload'] =
                params.data.wbsItem.estimatedWorkload
              params.data.wbsItem.estimatedWorkload = newWorkload
              store.dispatch(requireSave())
              return true
            },
            valueGetter: (params: ValueGetterParams<ProductBacklogRow>) => {
              const data = params.data
              if (!data) return undefined
              if (isSprintRow(data)) {
                const childPbiRows = getFilteredLeafChildren(params.node)
                // 同じ成果物（wbsItem.uuid）が重複しないようにユニーク化
                const seenWbsItemUuids = new Set<string>()
                return childPbiRows.reduce((total, row) => {
                  if (!row.data || !isProductBacklogItemRow(row.data)) {
                    return total
                  }
                  const pbiRow = row.data
                  // DISCARD状態の成果物は集計から除外
                  if (pbiRow.wbsItem.status === WbsItemStatus.DISCARD) {
                    return total
                  }
                  // 同じ成果物は1回だけカウント
                  const wbsItemUuid = pbiRow.wbsItem.uuid
                  if (!wbsItemUuid || seenWbsItemUuids.has(wbsItemUuid)) {
                    return total
                  }
                  seenWbsItemUuids.add(wbsItemUuid)

                  const workload: number = getWorkload(
                    pbiRow,
                    params.context.workloadUnitState?.selectedWorkloadUnit
                  )
                  return total + workload
                }, 0)
              }
              return getWorkloadUnitValue(
                data.wbsItem.estimatedWorkload,
                params.context.workloadUnitState?.selectedWorkloadUnit
              )
            },
          },
          {
            externalId: 'productBacklog.wbsItem.estimatedWorkload.per.sprint',
            ...estimatedWorkLoadDeliverableCol,
            field: FIELD_WBSITEM_ESTIMATED_WORKLOAD_DELIVERABLE_PER_SPRINT,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            editable: EDITABLE,
            hide: false,
            headerName: intl.formatMessage({
              id: 'productBacklog.estimatedWorkload.per.sprint',
            }),
            cellClassRules: {
              'grid-edited-cell': (
                params: CellClassParams<ProductBacklogRow>
              ) => {
                if (!params.data || !isProductBacklogItemRow(params.data)) {
                  return false
                }
                const pbiRow = params.data
                const parentSprintUuid = pbiRow.treeValue[0]
                if (!parentSprintUuid) return false
                // 行UUIDを含めた一意のキーを生成
                const key = createSprintProductItemKey(
                  parentSprintUuid,
                  pbiRow.uuid
                )
                // editedDataにキーが存在する = 変更された
                return pbiRow.editedData?.hasOwnProperty(key) ?? false
              },
            },
            cellStyle: getDeliverableSprintVarianceCellStyle,
            cellRenderer: EstimatedWorkloadCellRenderer,
            valueGetter: (params: ValueGetterParams<ProductBacklogRow>) => {
              const data = params.data
              if (!data) return undefined

              if (isSprintRow(data)) {
                return getSprintRowEstimatedWorkload(params)
              }

              if (isProductBacklogItemRow(data)) {
                return getPBIRowEstimatedWorkload(params)
              }

              return undefined
            },
            valueSetter: (params: ValueSetterParams<ProductBacklogRow>) => {
              return handleSprintProductItemChange(params, true)
            },
          },
          // タスク見積もり列の共通設定
          ...[
            {
              externalId: 'productBacklog.wbsItem.estimatedWorkload.task.total',
              messageId: 'productBacklog.estimatedWorkload.task.total',
              extractor: extractTotalTaskEstimate,
              cellStyle: getTotalTaskVarianceCellStyle,
              cellRenderer: TotalTaskEstimatedWorkloadCellRenderer,
            },
            {
              externalId:
                'productBacklog.wbsItem.estimatedWorkload.task.planned.total',
              messageId: 'productBacklog.estimatedWorkload.task.planned.total',
              extractor: extractSprintTaskEstimate,
              cellStyle: getTaskVarianceCellStyle,
              cellRenderer: TaskEstimatedWorkloadCellRenderer,
            },
          ].map(
            ({
              externalId,
              messageId,
              extractor,
              cellStyle,
              cellRenderer,
            }) => ({
              externalId,
              ...estimatedWorkLoadDeliverableCol,
              field: undefined,
              sortable: false,
              editable: false,
              hide: false,
              headerName: intl.formatMessage({ id: messageId }),
              valueGetter: createTaskEstimateValueGetter(extractor),
              cellStyle,
              cellRenderer,
            })
          ),
          {
            externalId: 'productBacklog.wbsItem.estimatedStoryPoint',
            ...estimatedStoryPoint,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            editable: EDITABLE,
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
        ] as FlagxsColumnDef[],
      },
      // Path
      {
        externalId: 'productBacklog.path',
        headerName: intl.formatMessage({ id: 'wbsItem.property.path' }),
        children: [
          {
            externalId: 'productBacklog.wbsItem.path',
            ...pathCol,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            editable: EDITABLE,
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.parentWbsItem.displayName',
            ...parentWbsItemCol,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            valueGetter: (params: ValueGetterParams<ProductBacklogRow>) => {
              if (!params.data) return undefined
              if (isProductBacklogItemRow(params.data)) {
                return params.data.parentWbsItem
              }
              return undefined
            },
            editable: EDITABLE,
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
        ] as FlagxsColumnDef[],
      },
      // Name
      {
        externalId: 'productBacklog.wbsItem.name',
        headerName: intl.formatMessage({ id: 'wbsItem.property.displayName' }),
        children: [
          {
            externalId: 'productBacklog.wbsItem.priority',
            ...priorityCol,
            width: 65,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            editable: EDITABLE,
            valueGetter: (params: ValueGetterParams<ProductBacklogRow>) => {
              if (!params.data) return undefined
              if (isSprintRow(params.data)) return undefined
              if (isProductBacklogItemRow(params.data)) {
                return params.data.wbsItem.priority
              }
              return undefined
            },
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.wbsItem.difficulty',
            ...difficultyCol,
            width: 65,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            editable: EDITABLE,
            valueGetter: (params: ValueGetterParams<ProductBacklogRow>) => {
              if (!params.data) return undefined
              if (isSprintRow(params.data)) return undefined
              if (isProductBacklogItemRow(params.data)) {
                return params.data.wbsItem.difficulty
              }
              return undefined
            },
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
        ] as FlagxsColumnDef[],
      },
      // Explanation
      {
        externalId: 'productBacklog.explanation',
        headerName: intl.formatMessage({
          id: 'projectPlan.description.comment',
        }),
        children: [
          {
            externalId: 'productBacklog.wbsItem.description',
            ...descriptionCol,
            width: 165,
          },
          {
            externalId: 'productBacklog.commentSummary.latestComment',
            ...latestCommentCol,
            width: 235,
          },
        ] as FlagxsColumnDef[],
      },
      // Assignment
      {
        externalId: 'productBacklog.assignment',
        headerName: intl.formatMessage({ id: 'projectPlan.assignment' }),
        children: [
          {
            externalId: 'productBacklog.wbsItem.accountable',
            ...accountableCol,
            cellEditor: ProjectMemberCellEditor,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            editable: EDITABLE,
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.wbsItem.responsible',
            ...responsibleCol,
            cellEditor: ProjectMemberCellEditor,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            editable: EDITABLE,
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.wbsItem.assignee',
            ...assigneeCol,
            cellEditor: ProjectMemberCellEditor,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            editable: EDITABLE,
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.wbsItem.watchers',
            ...watchersCol,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            editable: EDITABLE,
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
        ] as FlagxsColumnDef[],
      },
      // Sprint (Sprint×PBI単位の項目)
      {
        externalId: 'productBacklog.sprint',
        headerName: intl.formatMessage({ id: 'wbsItem.sprint' }),
        children: [
          {
            externalId: 'productBacklog.sprintGoal',
            field: 'sprintGoal',
            headerName: intl.formatMessage({ id: 'productBacklog.sprintGoal' }),
            width: 200,
            editable: EDITABLE,
            sortable: false,
            valueGetter: (params: ValueGetterParams<ProductBacklogRow>) => {
              if (!params.data || !isProductBacklogItemRow(params.data)) {
                return undefined
              }
              return params.data.sprintGoal
            },
            valueSetter: createSprintProductItemFieldValueSetter<string | null>(
              {
                keyGenerator: createSprintProductItemSprintGoalKey,
                getEditedData: pbiRow => pbiRow.sprintGoal,
                updateRowField: (pbiRow, value) => {
                  pbiRow.sprintGoal = value === '' ? null : value
                },
                updateSprintProductItem: (spi, value) => ({
                  ...spi,
                  sprintGoal: value === '' ? null : value,
                }),
                createNewSprintProductItem: (sprintUuid, value) => ({
                  uuid: '',
                  sprintUuid,
                  estimatedHour: 0,
                  taskEstimatedHour: 0,
                  displayOrder: 0,
                  lockVersion: 0,
                  assignee: undefined,
                  sprintGoal: value === '' ? null : value,
                }),
              }
            ),
            cellClassRules: {
              ...DISABLE_CELL_CLASS_OF_SPRINT_ROW,
              'grid-edited-cell': (
                params: CellClassParams<ProductBacklogRow>
              ) =>
                isSprintProductItemFieldEdited(
                  params,
                  createSprintProductItemSprintGoalKey
                ),
            },
          },
          {
            externalId: 'productBacklog.pbiAssignee',
            field: 'assignee',
            headerName: intl.formatMessage({
              id: 'productBacklog.pbiAssignee',
            }),
            width: 120,
            editable: EDITABLE,
            sortable: false,
            type: [ColumnType.autocomplete],
            cellRenderer: EntitySearchCellRenderer,
            cellEditor: ProjectMemberCellEditor,
            valueGetter: (params: ValueGetterParams<ProductBacklogRow>) => {
              if (!params.data || !isProductBacklogItemRow(params.data)) {
                return undefined
              }
              return params.data.assignee
            },
            valueSetter:
              createSprintProductItemFieldValueSetter<UserBasic | null>({
                keyGenerator: createSprintProductItemAssigneeKey,
                getEditedData: pbiRow => ({ assignee: pbiRow.assignee }),
                updateRowField: (pbiRow, value) => {
                  pbiRow.assignee = value ?? undefined
                },
                updateSprintProductItem: (spi, value) => ({
                  ...spi,
                  assignee: value ?? undefined,
                }),
                createNewSprintProductItem: (sprintUuid, value) => ({
                  uuid: '',
                  sprintUuid,
                  estimatedHour: 0,
                  taskEstimatedHour: 0,
                  displayOrder: 0,
                  lockVersion: 0,
                  assignee: value ?? undefined,
                  sprintGoal: null,
                }),
              }),
            suppressKeyboardEvent: params =>
              (!params.editing &&
                ['Delete', 'Backspace'].includes(params.event.key)) ||
              (params.editing && params.event.key === 'Enter'),
            filter: ClientSideSelectFilter,
            floatingFilter: true,
            filterParams: {
              valueGetter: ({ node }) => {
                if (!node?.data || !isProductBacklogItemRow(node.data)) {
                  return undefined
                }
                return node.data.assignee
              },
              getValue: (v?: UserBasic) => v?.uuid,
              getLabel: (v?: UserBasic) => v?.name,
            },
            comparator: (valueA: UserBasic, valueB: UserBasic) =>
              (valueA?.name ?? '').localeCompare(valueB?.name ?? ''),
            cellClassRules: {
              ...DISABLE_CELL_CLASS_OF_SPRINT_ROW,
              'grid-edited-cell': (
                params: CellClassParams<ProductBacklogRow>
              ) =>
                isSprintProductItemFieldEdited(
                  params,
                  createSprintProductItemAssigneeKey
                ),
            },
          },
        ] as FlagxsColumnDef[],
      },
      // Progress
      {
        externalId: 'productBacklog.progress',
        headerName: intl.formatMessage({ id: 'projectOverview.progress' }),
        hide: true,
        children: [
          {
            externalId: 'productBacklog.estimatedProgressRate',
            ...estimatedProgressRateCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.progressRate',
            ...progressRateCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.plannedValue',
            ...plannedValueCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.earnedValue',
            ...earnedValueCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.preceding',
            ...precedingCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.delayedWorkload',
            ...delayedCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.remainingWorkload',
            ...remainingCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
        ] as FlagxsColumnDef[],
      },
      // Productivity
      {
        externalId: 'ticket.list.productivity',
        headerName: intl.formatMessage({ id: 'projectOverview.evm' }),
        hide: true,
        children: [
          {
            externalId: 'productBacklog.wbsItem.actualHour',
            ...actualHourCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.costVariance',
            ...costVarianceCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.costPerformanceIndex',
            ...costPerformanceIndexCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.scheduleVariance',
            ...scheduleVarianceCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.schedulePerformanceIndex',
            ...schedulePerformanceIndexCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.estimateToComplete',
            ...estimateToCompleteCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.estimateAtCompletion',
            ...estimateAtCompletionCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.varianceAtCompletion',
            ...varianceAtCompletionCol,
            hide: true,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
        ] as FlagxsColumnDef[],
      },
      // Term
      {
        externalId: 'productBacklog.wbsItem.term',
        headerName: intl.formatMessage({ id: 'projectPlan.term' }),
        children: [
          {
            ...sprintsCol,
            externalId: 'productBacklog.wbsItem.sprints',
            editable: false,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
            // 重複を排除して表示（同一スプリント内に複数SPIがある場合の対応）
            valueGetter: (
              params: ValueGetterParams<ProductBacklogRow>
            ): SprintDetail[] | undefined => {
              if (!params.data || !isProductBacklogItemRow(params.data)) {
                return undefined
              }
              const sprints = params.data.wbsItem?.sprints
              if (!sprints || sprints.length === 0) return sprints
              // UUIDで重複排除
              const uniqueSprints: SprintDetail[] = []
              const seenUuids = new Set<string>()
              for (const sprint of sprints) {
                if (!sprint) continue
                if (!seenUuids.has(sprint.uuid)) {
                  seenUuids.add(sprint.uuid)
                  uniqueSprints.push(sprint)
                }
              }
              return uniqueSprints
            },
            cellRendererParams: {
              ...(sprintsCol.cellRendererParams || {}),
              tooltip: (params: ICellRendererParams<ProductBacklogRow>) => {
                if (params.data && isProductBacklogItemRow(params.data)) {
                  return intl.formatMessage({
                    id: 'productBacklog.sprintCell.tooltip.not.editable',
                  })
                }
                return undefined
              },
            },
          },
          {
            externalId: 'productBacklog.wbsItem.scheduledDate.startDate',
            ...scheduledStartDateCol,
            editable: EDITABLE,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.wbsItem.scheduledDate.endDate',
            ...scheduledEndDateCol,
            editable: EDITABLE,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.wbsItem.actualDate.startDate',
            ...actualStartDateCol,
            editable: EDITABLE,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.wbsItem.actualDate.endDate',
            ...actualEndDateCol,
            editable: EDITABLE,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
        ] as FlagxsColumnDef[],
      },
      // Update info
      {
        externalId: 'productBacklog.wbsItem.updateInfo',
        headerName: intl.formatMessage({ id: 'changeLog' }),
        children: [
          {
            externalId: 'productBacklog.wbsItem.revision',
            ...revisionCol,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.wbsItem.createdBy',
            ...createdByCol,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.wbsItem.createdAt',
            ...createdAtCol,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.wbsItem.updatedBy',
            ...updatedByCol,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
          {
            externalId: 'productBacklog.wbsItem.updatedAt',
            ...updatedAtCol,
            sortable: false, // TODO Remove and implement after considering sorting specification.
            cellClassRules: DISABLE_CELL_CLASS_OF_SPRINT_ROW,
          },
        ],
      },
    ],
    [
      dragAndDropCol,
      codeCol,
      statusCol,
      actionCol,
      attachmentsCol,
      tagsCol,
      estimatedWorkLoadDeliverableCol,
      estimatedStoryPoint,
      pathCol,
      parentWbsItemCol,
      priorityCol,
      difficultyCol,
      descriptionCol,
      latestCommentCol,
      accountableCol,
      responsibleCol,
      assigneeCol,
      watchersCol,
      estimatedProgressRateCol,
      progressRateCol,
      plannedValueCol,
      earnedValueCol,
      precedingCol,
      delayedCol,
      remainingCol,
      actualHourCol,
      costVarianceCol,
      costPerformanceIndexCol,
      scheduleVarianceCol,
      schedulePerformanceIndexCol,
      estimateToCompleteCol,
      estimateAtCompletionCol,
      varianceAtCompletionCol,
      sprintsCol,
      scheduledStartDateCol,
      scheduledEndDateCol,
      actualStartDateCol,
      actualEndDateCol,
      revisionCol,
      createdByCol,
      createdAtCol,
      updatedByCol,
      updatedAtCol,
      onClickedStatusCell,
      openSprintAdjustmentDialog,
    ]
  )
  return columnDefs
}

const EDITABLE = (params: EditableCallbackParams<ProductBacklogRow>) => {
  if (!params.data) return false
  if (isSprintRow(params.data)) return false
  if (isProductBacklogItemRow(params.data)) return true
  return false
}

const getSprintStatusColor = (status: SprintStatus): string => {
  switch (status) {
    case SprintStatus.INPROGRESS:
      return getWbsItemStatusColorCode(WbsItemStatus.DOING) // WBSのDOING色 - 進行中
    case SprintStatus.FINISHED:
      return getWbsItemStatusColorCode(WbsItemStatus.DONE) // WBSのDONE色 - 完了
    case SprintStatus.STANDBY:
      return getWbsItemStatusDeepColorCode(WbsItemStatus.TODO) // getWbsItemStatusColorCodeだと色がつかないためここだけDeepColorCodeを使用
    case SprintStatus.CANCELED:
      return getWbsItemStatusColorCode(WbsItemStatus.DISCARD) // WBSのDISCARD色 - 中止
    default:
      return '#9E9E9E' // Gray - デフォルト
  }
}

// WbsItemステータスのソート順
const WBS_ITEM_STATUS_ORDER: WbsItemStatus[] = [
  WbsItemStatus.TODO,
  WbsItemStatus.DOING,
  WbsItemStatus.REVIEW,
  WbsItemStatus.DONE,
  WbsItemStatus.DISCARD,
]

// WbsItemステータスの比較関数
const wbsItemStatusComparator = (a: WbsItemStatus, b: WbsItemStatus) =>
  WBS_ITEM_STATUS_ORDER.findIndex(s => s === a) -
  WBS_ITEM_STATUS_ORDER.findIndex(s => s === b)
// スプリント行の進捗を計算する関数
// 注: 進捗バーはフィルター状態に関係なく全データを表示する
const getSprintProgress = (
  params: ICellRendererParams<ProductBacklogRow>
): { completed: number; total: number } => {
  if (!params.node || !params.data || !isSprintRow(params.data)) {
    return { completed: 0, total: 0 }
  }

  const childNodes = allDescendants(params.node)
  let completed = 0
  let total = 0

  childNodes.forEach((childNode: IRowNode<ProductBacklogRow>) => {
    if (childNode.data && isProductBacklogItemRow(childNode.data)) {
      total++
      const status = childNode.data.wbsItem.status
      if (status === WbsItemStatus.DONE) {
        completed++
      }
    }
  })

  return { completed, total }
}
const DISABLE_CELL_CLASS_OF_SPRINT_ROW = {
  'ag-cell-hidden-and-disable': (params: CellClassParams<ProductBacklogRow>) =>
    !params.data || !isProductBacklogItemRow(params.data),
} as const

// SprintProductItem単位のフィールドの編集状態を判定するヘルパー関数
const isSprintProductItemFieldEdited = (
  params: CellClassParams<ProductBacklogRow>,
  keyGenerator: (sprintUuid: string, rowUuid?: string) => string
): boolean => {
  if (!params.data || !isProductBacklogItemRow(params.data)) {
    return false
  }
  const pbiRow = params.data
  const parentSprintUuid = pbiRow.treeValue[0]
  if (!parentSprintUuid) return false
  // 行UUID（SprintProductItemのUUID）も渡して一意のキーを生成
  const key = keyGenerator(parentSprintUuid, pbiRow.uuid)
  return pbiRow.editedData?.hasOwnProperty(key) ?? false
}

// SprintProductItem単位のフィールドのvalueSetterを生成するヘルパー関数
const createSprintProductItemFieldValueSetter =
  <T>(config: {
    keyGenerator: (sprintUuid: string, rowUuid?: string) => string
    getEditedData: (pbiRow: ProductBacklogItemRow) => unknown
    updateRowField: (pbiRow: ProductBacklogItemRow, value: T) => void
    updateSprintProductItem: (
      spi: SprintProductItemDetail,
      value: T
    ) => SprintProductItemDetail
    createNewSprintProductItem: (
      sprintUuid: string,
      value: T
    ) => SprintProductItemDetail
  }) =>
  (params: ValueSetterParams<ProductBacklogRow>): boolean => {
    if (!params.data || !isProductBacklogItemRow(params.data)) {
      return false
    }
    const pbiRow = params.data
    const parentSprintUuid = pbiRow.treeValue[0]
    if (!parentSprintUuid) return false

    const newValue = params.newValue as T
    // 行UUID（SprintProductItemのUUID）も渡して一意のキーを生成
    const key = config.keyGenerator(parentSprintUuid, pbiRow.uuid)

    if (!pbiRow.editedData) {
      pbiRow.editedData = {}
    }
    if (!pbiRow.editedData.hasOwnProperty(key)) {
      pbiRow.editedData[key] = config.getEditedData(pbiRow)
    }

    config.updateRowField(pbiRow, newValue)

    // 行のUUID（SprintProductItemのUUID）で検索
    // NOTE: sprintUuidでのフォールバック検索は行わない
    // 同一スプリントに複数のSprintProductItemが存在しうるため、
    // sprintUuidでの検索は誤った項目を見つける可能性がある
    const existingIndex = pbiRow.sprintProductItems.findIndex(
      spi => spi.uuid === pbiRow.uuid
    )
    if (existingIndex >= 0) {
      pbiRow.sprintProductItems[existingIndex] = config.updateSprintProductItem(
        pbiRow.sprintProductItems[existingIndex],
        newValue
      )
    } else {
      const newSpi = config.createNewSprintProductItem(
        parentSprintUuid,
        newValue
      )
      if (!newSpi.uuid) {
        newSpi.uuid = pbiRow.uuid
      }
      pbiRow.sprintProductItems.push(newSpi)
    }

    pbiRow.edited = true
    store.dispatch(requireSave())
    return true
  }

export const FIELD_WBSITEM_ESTIMATED_WORKLOAD_DELIVERABLE: string =
  'wbsItem.estimatedWorkload'
export const FIELD_WBSITEM_ESTIMATED_WORKLOAD_DELIVERABLE_PER_SPRINT: string =
  'wbsItem.estimatedWorkload.perSprint'
export const FIELD_WBSITEM_SPRINTS: string = 'wbsItem.sprints'
export const FIELD_WBSITEM_STATUS: string = 'wbsItem.status'
